import clsx from "clsx";
import type { PhoneBoardView } from "../../store/uiStore";
import { Button } from "../Button";
import { FormInput } from "../FormFields";
import { SegmentedControl } from "../SegmentedControl";

interface PhoneBoardToolbarProps {
  view: PhoneBoardView;
  /** Over the Map, unless the results card covers it. */
  floating: boolean;
  /** Omitted offline, when there is no map to switch to. */
  onViewChange?: (view: PhoneBoardView) => void;
  search: string;
  onSearchChange: (value: string) => void;
  activeFilterCount: number;
  onOpenFilters: () => void;
}

/**
 * Phone trip board toolbar: Planner/Map switch, POI search and the Filters
 * button, the only way to change filters. Solid above the Planner and the
 * full-height results; over the Map it has no background and its controls
 * float with a shadow.
 */
export function PhoneBoardToolbar({
  view,
  floating,
  onViewChange,
  search,
  onSearchChange,
  activeFilterCount,
  onOpenFilters,
}: PhoneBoardToolbarProps) {
  return (
    <div
      data-testid="phone-toolbar"
      className={clsx(
        "px-3 py-2",
        floating
          ? "pointer-events-none"
          : "shrink-0 border-b border-border bg-surface",
      )}
    >
      <div className="pointer-events-auto flex items-center gap-2">
        {onViewChange && (
          <div className={clsx("shrink-0 rounded-lg", floating && "shadow-md")}>
            <SegmentedControl
              aria-label="Board view"
              size="lg"
              value={view}
              onChange={onViewChange}
              options={[
                {
                  value: "planner",
                  label: "",
                  icon: "calendar_month",
                  ariaLabel: "Planner",
                },
                { value: "map", label: "", icon: "map", ariaLabel: "Map" },
              ]}
            />
          </div>
        )}
        <div
          className={clsx(
            "relative min-w-0 flex-1 rounded-lg",
            floating && "bg-surface shadow-md",
          )}
        >
          <span
            className="material-symbols-outlined pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-lg text-secondary"
            aria-hidden
          >
            search
          </span>
          <FormInput
            type="search"
            aria-label="Search POIs"
            placeholder="Search POIs"
            value={search}
            onChange={onSearchChange}
            className="min-h-11 pl-9"
          />
        </div>
        <Button
          variant={activeFilterCount > 0 ? "primary" : "secondary"}
          icon="tune"
          onClick={onOpenFilters}
          aria-label={
            activeFilterCount > 0
              ? `Filters, ${activeFilterCount} active`
              : "Filters"
          }
          className={clsx("min-h-11 shrink-0 px-3", floating && "shadow-md")}
        >
          <span className="hidden min-[380px]:inline">Filters</span>
          {activeFilterCount > 0 && (
            <span className="grid h-5 min-w-5 place-items-center rounded-full bg-surface px-1 text-2xs font-bold tabular-nums text-primary">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </div>
    </div>
  );
}
